import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useClinic } from '@/contexts/ClinicContext';
import { Building2, MapPin, Phone, Mail, Loader2, LogOut, CheckCircle, Shield, Heart, Activity, LayoutDashboard } from 'lucide-react';
import { Clinic } from '@/models/clinic.model';
import { Skeleton } from '@/components/ui/skeleton';
import { getFooterText } from '@/config/version';
import { OrganizationMembership } from '@/services/types';

const ClinicSelection = () => {
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [selectedClinicId, setSelectedClinicId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSelecting, setIsSelecting] = useState(false);
  const navigate = useNavigate();
  const { user, selectClinic,checkAuth } = useAuth();
  const { getClinic } = useClinic();
  const { toast } = useToast();

  useEffect(() => {
    const loadClinics = async () => {
      try {
        setIsLoading(true);
        let loadedClinics: Clinic[] = [];
        
        // Check if user has clinics from login response
        if (user?.clinics && user.clinics.length > 0) {
          loadedClinics = user.clinics;
          setClinics(loadedClinics);
        } else if (user?.practitionerId) {
          // Fallback: fetch clinics using practitioner service
          try {
            const { authService } = await import('@/services/authService');
            const response = await authService.getPractitionerClinics();
            if (response.data?.clinics) {
              loadedClinics = response.data.clinics;
              setClinics(loadedClinics);
            }
          } catch (fetchError) {
            console.error('Error fetching clinics:', fetchError);
            toast({
              title: 'Error',
              description: 'Failed to load clinics. Please try again.',
              variant: 'destructive',
            });
          }
        }
        
        // Check localStorage for previously selected clinic
        const storedClinicId = localStorage.getItem('selectedClinicId');
        if (storedClinicId && loadedClinics.length > 0 && loadedClinics.some(c => c.id === storedClinicId)) {
          setSelectedClinicId(storedClinicId);
        }
      } catch (error) {
        console.error('Error loading clinics:', error);
        toast({
          title: 'Error',
          description: 'Failed to load clinics. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      loadClinics();
    } else {
      setIsLoading(false);
    }
  }, [user, toast]);

  const handleSelectClinic = async (clinicId: string) => {
    try {
      setIsSelecting(true);
      setSelectedClinicId(clinicId);

      // Store selected clinic in localStorage
      localStorage.setItem('selectedClinicId', clinicId);
      localStorage.setItem('selectedClinicTimestamp', Date.now().toString());

      // Select clinic via API
      const response = await selectClinic(clinicId);
      
      // Get clinic details
      const clinic = await getClinic(clinicId);
      
      toast({
        title: 'Success',
        description: 'Clinic selected successfully!',
        variant: 'success',
      });

      // Redirect based on role
      if (clinic?.is_admin) {
        navigate('/', { replace: true });
      } else if (clinic?.is_practitioner) {
        navigate('/practitioner-portal', { replace: true });
      } else if (clinic?.is_clinic_staff) {
        navigate('/', { replace: true });
      }
    } catch (error: any) {
      console.error('Error selecting clinic:', error);
      toast({
        title: 'Error',
        description: error?.message || 'Failed to select clinic. Please try again.',
        variant: 'destructive',
      });
      setSelectedClinicId(null);
      await checkAuth();
    } finally {
      setIsSelecting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('selectedClinicId');
    localStorage.removeItem('selectedClinicTimestamp');
    localStorage.removeItem('selectedOrgId');
    navigate('/login');
  };

  // Loading skeleton
  const ClinicCardSkeleton = () => (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
      <div className="flex items-center gap-3">
        <Skeleton className="h-12 w-12 rounded-xl" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-20" />
        </div>
      </div>
      <div className="space-y-3 pt-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
      <Skeleton className="h-11 w-full rounded-xl mt-4" />
    </div>
  );

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left Side - Branded Visual */}
      <div className="hidden lg:flex w-[480px] flex-shrink-0 relative overflow-hidden bg-gradient-to-br from-[#2D6A78] via-brand-teal to-[#356872] h-screen">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        {/* Floating Elements */}
        <motion.div
          className="absolute top-20 left-16 w-16 h-16 bg-white/10 rounded-2xl backdrop-blur-sm flex items-center justify-center"
          animate={{ y: [0, -15, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        >
          <Shield className="w-8 h-8 text-white/80" />
        </motion.div>

        <motion.div
          className="absolute top-40 right-20 w-14 h-14 bg-white/10 rounded-xl backdrop-blur-sm flex items-center justify-center"
          animate={{ y: [0, 12, 0] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
        >
          <Heart className="w-7 h-7 text-white/80" />
        </motion.div>

        <motion.div
          className="absolute bottom-40 left-24 w-12 h-12 bg-white/10 rounded-lg backdrop-blur-sm flex items-center justify-center"
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        >
          <Activity className="w-6 h-6 text-white/80" />
        </motion.div>

        {/* Decorative Circles */}
        <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-white/5 rounded-full" />
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-white/5 rounded-full" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-white/5 rounded-full" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="w-16 h-16 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-8">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-4 leading-tight">
              Welcome Back,<br />
              <span className="text-white/90">{user?.name || 'Provider'}</span>
            </h2>
            <p className="text-base text-white/80 max-w-sm leading-relaxed">
              You have access to multiple clinics. Select the clinic you'd like to manage today.
            </p>

            {/* Stats */}
            <div className="flex gap-6 mt-10">
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
              >
                <p className="text-3xl font-bold">{clinics.length || '-'}</p>
                <p className="text-sm text-white/70 mt-1">Linked Clinics</p>
              </motion.div>
              {clinics.find(c => c.is_primary) && (
                <>
                  <div className="w-px bg-white/20" />
                  <motion.div
                    className="text-center"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    <p className="text-3xl font-bold truncate max-w-[140px] leading-9" title={clinics.find(c => c.is_primary)?.name}>{clinics.find(c => c.is_primary)?.name}</p>
                    <p className="text-sm text-white/70 mt-1">Primary</p>
                  </motion.div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Side - Clinic Selection */}
      <div className="w-full lg:flex-1 flex flex-col bg-background h-screen overflow-auto">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-4 lg:p-6 border-b border-slate-200 bg-white">
          <img
            src="/nimbus_logo.svg"
            alt="Nimbus Healthcare"
            className="h-8"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-slate-500 hover:text-slate-700 hover:bg-slate-100"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 py-8 lg:px-8 lg:py-10">
            {/* Mobile Header */}
            <motion.div 
              className="lg:hidden text-center mb-8"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="w-14 h-14 bg-brand-teal rounded-xl flex items-center justify-center mx-auto mb-4">
                <Building2 className="h-7 w-7 text-white" />
              </div>
              <h1 className="font-montserrat text-xl font-bold tracking-tight text-slate-900">Select a Clinic</h1>
              <p className="text-sm text-slate-500 mt-1 font-inter">Choose which clinic to access</p>
            </motion.div>

            {/* Desktop Header */}
            <motion.div 
              className="hidden lg:block mb-8"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="font-montserrat text-2xl font-bold tracking-tight text-slate-900">Select a Clinic</h1>
              <p className="text-slate-500 text-sm mt-1 font-inter">Choose which clinic you'd like to access</p>
            </motion.div>

            {/* Organization Switcher - only shown when user has org memberships */}
            {!isLoading && user?.organizationMemberships && user.organizationMemberships.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8"
              >
                <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Organizations</h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  {user.organizationMemberships.map((membership) => (
                    <div
                      key={membership.id}
                      className="group relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border-2 border-slate-700 hover:border-teal-500 transition-all duration-300 cursor-pointer overflow-hidden hover:shadow-lg hover:shadow-teal-500/10"
                      onClick={() => navigate(`/org/${membership.organization_id}`)}
                    >
                      <div className="p-5">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 rounded-xl bg-teal-600 flex items-center justify-center">
                            {membership.organization?.logo_url ? (
                              <img src={membership.organization.logo_url} alt="" className="w-8 h-8 rounded" />
                            ) : (
                              <LayoutDashboard className="h-6 w-6 text-white" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-white truncate">
                              {membership.organization?.name || 'Organization'}
                            </h3>
                            <span className="inline-flex items-center mt-1 px-2 py-0.5 text-xs font-medium text-teal-300 bg-teal-900/50 rounded-full capitalize">
                              {membership.role?.replace('org_', '')}
                            </span>
                          </div>
                        </div>
                        <Button
                          className="w-full mt-4 h-10 rounded-xl font-medium bg-teal-600 hover:bg-teal-500 text-white transition-all"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/org/${membership.organization_id}`);
                          }}
                        >
                          <LayoutDashboard className="h-4 w-4 mr-2" />
                          Open Org Dashboard
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-b border-slate-200 mt-8 mb-0" />
                <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mt-6 mb-3">Clinics</h2>
              </motion.div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="grid gap-4 sm:grid-cols-2">
                {[1, 2, 3, 4].map((i) => (
                  <ClinicCardSkeleton key={i} />
                ))}
              </div>
            )}

            {/* No Clinics State */}
            {!isLoading && (!clinics || clinics.length === 0) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8 border border-slate-200 text-center"
              >
                <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Building2 className="h-8 w-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No Clinics Available</h3>
                <p className="text-slate-500 text-sm mb-6">
                  You are not linked to any clinics. Please contact your administrator.
                </p>
                <Button 
                  onClick={() => navigate('/login')} 
                  className="h-11 px-6 rounded-xl bg-brand-teal hover:bg-brand-teal/90"
                >
                  Back to Login
                </Button>
              </motion.div>
            )}

            {/* Clinics Grid */}
            {!isLoading && clinics.length > 0 && (
              <div className="grid gap-4 sm:grid-cols-2">
                {clinics.map((clinic, index) => (
                  <motion.div
                    key={clinic.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.08 }}
                  >
                    <div
                      className={`group relative bg-white rounded-2xl border transition-all duration-300 cursor-pointer overflow-hidden ${
                        selectedClinicId === clinic.id
                          ? 'border-brand-teal shadow-lg shadow-brand-teal/10 ring-1 ring-brand-teal'
                          : 'border-slate-200 hover:border-slate-300 hover:shadow-lg hover:shadow-slate-200/50'
                      } ${isSelecting && selectedClinicId !== clinic.id ? 'opacity-50' : ''}`}
                      onClick={() => !isSelecting && handleSelectClinic(clinic.id)}
                    >
                      {/* Selection indicator */}
                      {selectedClinicId === clinic.id && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute top-3 right-3 w-7 h-7 bg-brand-teal rounded-full flex items-center justify-center z-10"
                        >
                          {isSelecting ? (
                            <Loader2 className="h-4 w-4 text-white animate-spin" />
                          ) : (
                            <CheckCircle className="h-4 w-4 text-white" />
                          )}
                        </motion.div>
                      )}

                      <div className="p-5">
                        {/* Clinic Header */}
                        <div className="flex items-start gap-3 mb-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                            selectedClinicId === clinic.id ? 'bg-brand-teal' : 'bg-brand-teal/10'
                          }`}>
                            <Building2 className={`h-6 w-6 transition-colors ${
                              selectedClinicId === clinic.id ? 'text-white' : 'text-brand-teal'
                            }`} />
                          </div>
                          <div className="flex-1 min-w-0 pr-8">
                            <h3 className="font-semibold text-slate-900 truncate">{clinic.name}</h3>
                            {clinic.is_primary && (
                              <span className="inline-flex items-center mt-1.5 px-2.5 py-0.5 text-xs font-medium text-brand-teal bg-brand-teal/10 rounded-full">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Primary
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Clinic Details */}
                        <div className="space-y-2.5 text-sm">
                          {clinic.address_line_1 && (
                            <div className="flex items-start gap-2.5 text-slate-600">
                              <MapPin className="h-4 w-4 mt-0.5 text-slate-400 flex-shrink-0" />
                              <div className="truncate">
                                <span>{clinic.address_line_1}</span>
                                {clinic.city && (
                                  <span className="text-slate-400"> · {clinic.city}, {clinic.postal_code}</span>
                                )}
                              </div>
                            </div>
                          )}
                          {clinic.primary_phone && (
                            <div className="flex items-center gap-2.5 text-slate-600">
                              <Phone className="h-4 w-4 text-slate-400" />
                              <span>{clinic.primary_phone}</span>
                            </div>
                          )}
                          {clinic.email && (
                            <div className="flex items-center gap-2.5 text-slate-600">
                              <Mail className="h-4 w-4 text-slate-400" />
                              <span className="truncate">{clinic.email}</span>
                            </div>
                          )}
                        </div>

                        {/* NPI */}
                        {clinic.npi && (
                          <div className="mt-4 pt-3 border-t border-slate-100">
                            <p className="text-xs text-slate-400">NPI: <span className="text-slate-500 font-medium">{clinic.npi}</span></p>
                          </div>
                        )}

                        {/* Select Button */}
                        <Button
                          className="w-full mt-4 h-11 rounded-xl font-medium transition-all bg-brand-teal hover:bg-brand-teal/90 text-white"
                          disabled={isSelecting}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectClinic(clinic.id);
                          }}
                        >
                          {isSelecting && selectedClinicId === clinic.id ? (
                            <span className="flex items-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Connecting...
                            </span>
                          ) : (
                            'Select Clinic'
                          )}
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-4 lg:p-6 text-center border-t border-slate-100 bg-white">
          <p className="text-xs text-slate-400">
            {getFooterText()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClinicSelection;
